import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import EndOfVideoCard from './EndOfVideoCard';

// Carga perezosa y compartida de la YouTube IFrame API — un solo <script>
// para toda la sesión, sin importar cuántos reproductores se monten.
// Ver openspec/changes/academy-lesson-watch-time-completion (design.md D3).
let apiLoadPromise: Promise<void> | null = null;

function loadYoutubeIframeApi(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve();
  if ((window as any).YT?.Player) return Promise.resolve();
  if (apiLoadPromise) return apiLoadPromise;

  apiLoadPromise = new Promise((resolve) => {
    const previousCallback = (window as any).onYouTubeIframeAPIReady;
    (window as any).onYouTubeIframeAPIReady = () => {
      previousCallback?.();
      resolve();
    };
    const script = document.createElement('script');
    script.src = 'https://www.youtube.com/iframe_api';
    script.async = true;
    document.head.appendChild(script);
  });
  return apiLoadPromise;
}

type Props = {
  videoId: string;
  title: string;
  initialTime?: number;
  onEnded?: () => void;
  onTimeUpdate?: (currentTime: number, duration: number) => void;
  /**
   * Se dispara exactamente una vez por montaje, tan pronto como el player
   * reporta una duración real (segundos redondeados), o con `null` si tras
   * un breve margen no logró obtenerla (adblocker, fallo de carga del
   * IFrame, etc.) — esta es la señal de "lección abierta" que el backend
   * usa para el fallback transitorio mientras Lesson.youtubeDurationSeconds
   * sigue sin capturarse. Ver design.md D3.
   */
  onOpened?: (durationSecondsIfKnown: number | null) => void;
  /** Siguiente lección accesible: habilita la tarjeta de cierre con cuenta atrás cancelable. */
  nextLesson?: { title: string; onNext: () => void } | null;
  /** Por defecto true (comportamiento histórico); false deja el video en pausa al abrir. */
  autoPlay?: boolean;
};

const TIME_UPDATE_POLL_MS = 1000;
const DURATION_CAPTURE_TIMEOUT_MS = 4000;

export default function YoutubeLessonPlayer({
  videoId,
  title,
  initialTime = 0,
  onEnded,
  onTimeUpdate,
  onOpened,
  nextLesson,
  autoPlay = true,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<any>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const openedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const openedReportedRef = useRef(false);
  const autoPlayRef = useRef(autoPlay);
  const [ended, setEnded] = useState(false);

  // Refs para no reconstruir el player en cada re-render cuando cambian los callbacks.
  const onEndedRef = useRef(onEnded);
  const onTimeUpdateRef = useRef(onTimeUpdate);
  const onOpenedRef = useRef(onOpened);
  useLayoutEffect(() => { onEndedRef.current = onEnded; }, [onEnded]);
  useLayoutEffect(() => { onTimeUpdateRef.current = onTimeUpdate; }, [onTimeUpdate]);
  useLayoutEffect(() => { onOpenedRef.current = onOpened; }, [onOpened]);

  useEffect(() => {
    let cancelled = false;
    openedReportedRef.current = false;

    const reportOpenedOnce = (durationSecondsIfKnown: number | null) => {
      if (openedReportedRef.current) return;
      openedReportedRef.current = true;
      if (openedTimeoutRef.current) clearTimeout(openedTimeoutRef.current);
      onOpenedRef.current?.(durationSecondsIfKnown);
    };

    loadYoutubeIframeApi().then(() => {
      if (cancelled || !containerRef.current) return;
      const YT = (window as any).YT;

      playerRef.current = new YT.Player(containerRef.current, {
        videoId,
        playerVars: {
          rel: 0,
          modestbranding: 1,
          playsinline: 1,
          iv_load_policy: 3,
          color: 'white',
          autoplay: autoPlayRef.current ? 1 : 0,
          start: Math.max(0, Math.floor(initialTime)),
        },
        events: {
          onReady: () => {
            const duration = playerRef.current?.getDuration?.() ?? 0;
            if (duration > 0) {
              reportOpenedOnce(Math.round(duration));
            } else {
              openedTimeoutRef.current = setTimeout(() => reportOpenedOnce(null), DURATION_CAPTURE_TIMEOUT_MS);
            }

            pollRef.current = setInterval(() => {
              const player = playerRef.current;
              if (!player?.getCurrentTime) return;
              const currentTime = player.getCurrentTime();
              const dur = player.getDuration?.() ?? 0;
              if (dur > 0) {
                onTimeUpdateRef.current?.(currentTime, dur);
                reportOpenedOnce(Math.round(dur));
              }
            }, TIME_UPDATE_POLL_MS);
          },
          onStateChange: (event: { data: number }) => {
            // YT.PlayerState.ENDED === 0, PLAYING === 1
            if (event.data === 0) {
              onEndedRef.current?.();
              setEnded(true);
            } else if (event.data === 1) {
              setEnded(false);
            }
          },
        },
      });
    });

    return () => {
      cancelled = true;
      if (pollRef.current) clearInterval(pollRef.current);
      if (openedTimeoutRef.current) clearTimeout(openedTimeoutRef.current);
      playerRef.current?.destroy?.();
      playerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId]);

  return (
    <div className="relative h-full w-full">
      <div className="h-full w-full" title={title} ref={containerRef} />
      {ended && (
        <EndOfVideoCard
          nextTitle={nextLesson?.title}
          onNext={nextLesson ? nextLesson.onNext : undefined}
          onReplay={() => {
            setEnded(false);
            playerRef.current?.seekTo?.(0, true);
            playerRef.current?.playVideo?.();
          }}
        />
      )}
    </div>
  );
}
